import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuoteLeft, faChevronLeft, faChevronRight, faStar } from '@fortawesome/free-solid-svg-icons';

const testimonials = [
  {
    id: 1,
    name: 'Sophie Martin',
    role: 'Propriétaire à Épinay-sous-Sénart',
    image: 'https://randomuser.me/api/portraits/women/44.jpg',
    content: 'MGE COUVERTURE a refait entièrement notre toiture avec un professionnalisme remarquable. Leur équipe a été à l\'écoute de nos besoins et a résolu des problèmes d\'infiltration que nous avions depuis des années. Travail impeccable !',
    rating: 5
  },
  {
    id: 2,
    name: 'Thomas Dubois',
    role: 'Propriétaire de maison ancienne',
    image: 'https://randomuser.me/api/portraits/men/32.jpg',
    content: 'Nous avons fait appel à MGE COUVERTURE pour la rénovation complète de notre toiture en ardoise. Leur expertise dans les matériaux traditionnels et leur souci du détail ont permis de préserver le caractère historique de notre demeure.',
    rating: 5
  },
  {
    id: 3,
    name: 'Marie Leroy',
    role: 'Syndic de copropriété',
    image: 'https://randomuser.me/api/portraits/women/68.jpg',
    content: 'Notre immeuble nécessitait des travaux d\'étanchéité urgents. MGE COUVERTURE a réalisé un diagnostic précis et proposé des solutions adaptées. Intervention rapide et efficace qui a résolu tous nos problèmes d\'infiltration.',
    rating: 4
  },
  {
    id: 4,
    name: 'Jean Moreau',
    role: 'Architecte',
    image: 'https://randomuser.me/api/portraits/men/75.jpg',
    content: 'Je collabore régulièrement avec MGE COUVERTURE pour mes projets de rénovation. Leur expertise en matière de toiture et d\'étanchéité, combinée à leur respect des normes actuelles, en fait un partenaire fiable et précieux.',
    rating: 5
  }
];

const Testimonials: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [autoplay, setAutoplay] = useState(true);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (autoplay) {
      interval = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
      }, 5000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoplay]);

  const handlePrev = () => {
    setAutoplay(false);
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const handleNext = () => {
    setAutoplay(false);
    setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
  };

  const renderStars = (rating: number) => {
    return Array(5).fill(0).map((_, i) => (
      <FontAwesomeIcon 
        key={i} 
        icon={faStar} 
        className={i < rating ? "text-yellow-400" : "text-gray-300"} 
      />
    ));
  };

  return (
    <section className="py-20 bg-gray-50 dark:bg-gray-800">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-16">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Ce que disent nos clients
          </h2>
          <p className="font-heading text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Découvrez les témoignages de nos clients satisfaits qui nous ont fait confiance pour leurs projets.
          </p>
        </div>

        <div className="relative max-w-4xl mx-auto">
          {/* Testimonial Slider */}
          <div className="overflow-hidden">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.5 }}
              className="bg-white dark:bg-gray-900 rounded-xl shadow-xl p-8 md:p-12"
            >
              <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
                <div className="flex-shrink-0">
                  <div className="relative">
                    <img 
                      src={testimonials[currentIndex].image} 
                      alt={testimonials[currentIndex].name}
                      className="w-24 h-24 rounded-full object-cover border-4 border-primary-light"
                    />
                    <div className="absolute -top-2 -left-2 w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                      <FontAwesomeIcon icon={faQuoteLeft} className="text-white text-sm" />
                    </div>
                  </div>
                </div>
                
                <div className="flex-1">
                  <div className="mb-4 flex">
                    {renderStars(testimonials[currentIndex].rating)}
                  </div>
                  
                  <p className="text-gray-700 dark:text-gray-300 text-lg italic mb-6">
                    "{testimonials[currentIndex].content}"
                  </p>
                  
                  <div>
                    <h4 className="font-heading text-xl font-semibold text-gray-900 dark:text-white">
                      {testimonials[currentIndex].name}
                    </h4>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-center mt-8 space-x-4">
            <button 
              onClick={handlePrev}
              className="w-12 h-12 rounded-full bg-white dark:bg-gray-700 shadow-md flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              aria-label="Témoignage précédent"
            >
              <FontAwesomeIcon icon={faChevronLeft} className="text-gray-700 dark:text-gray-200" />
            </button>
            
            <button 
              onClick={handleNext}
              className="w-12 h-12 rounded-full bg-white dark:bg-gray-700 shadow-md flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              aria-label="Témoignage suivant"
            >
              <FontAwesomeIcon icon={faChevronRight} className="text-gray-700 dark:text-gray-200" />
            </button>
          </div>
          
          {/* Indicators */}
          <div className="flex justify-center mt-6 space-x-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setAutoplay(false);
                  setCurrentIndex(index);
                }}
                className={`w-3 h-3 rounded-full transition-colors ${
                  index === currentIndex 
                    ? 'bg-primary' 
                    : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
                }`}
                aria-label={`Aller au témoignage ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
